// Shore Doors Locksmiths — pivot content layer (see SHORE_DOORS_PIVOT_PLAN.md)
import { business } from './business';
//
// locksmithLive: true (Hugh, 5 Jul 2026) — the site presents the full locksmith
// service range from day one. Setting it false re-gates the locksmith-dependent
// pages (noindex + "launching soon" banners + unlinked hub cards) if ever needed.

export const siteConfig = {
  brandName: "Shore Doors Locksmiths",
  // Hugh 5 Jul 2026: site presents the full service range from day one
  locksmithLive: true,
  // Hugh, 26 Sep 2026: lockouts and emergency call-outs 24/7 (the Google profile says "Open 24
  // hours"); other work runs in business hours. Outside business hours the after-hours rates apply.
  hoursLine: "Lockouts & emergencies 24/7",
  businessHoursLine: "Mon–Fri 7:00am – 6:00pm",
  afterHoursDays: "evenings, nights, weekends and public holidays",
  residentialArea: "Auckland's North Shore — wider Auckland by arrangement",
  commercialArea: "Greater Auckland",
};

// Rate card (plan §1 D7) — confirmed by Hugh 5 Jul 2026; after-hours rates Hugh, 26 Sep 2026.
// GST-EXCLUSIVE: every figure below is plus GST. GST is added at the end of the sale
// (owner ruling 2026-08-22). Any copy quoting these numbers must say "+ GST".
export const rates = {
  callout: 120, // includes travel + first 15 minutes on site
  block15: 30,
  afterHoursCallout: 225, // outside Mon–Fri 7am–6pm; includes travel + first 15 minutes on site
  afterHoursBlock15: 45,
  rekeyPerLockFrom: 60,
  keyCutFrom: 15,
  keyCutTo: 25,
};

export const afterHoursRateLine =
  `After hours (${siteConfig.afterHoursDays}): $${rates.afterHoursCallout} + GST call-out including travel and the first 15 minutes, then $${rates.afterHoursBlock15} + GST per 15 minutes.`;

export const fixedQuoteLine =
  "Fixed quote confirmed before any extra work starts — no surprises.";

export const residentialFaqs = [
  {
    id: "r1",
    question: "How much does a locksmith visit cost?",
    answer:
      `In business hours (${siteConfig.businessHoursLine}) our call-out is $${rates.callout} + GST, which covers travel and the first 15 minutes on site. After that, time is charged at $${rates.block15} + GST per 15-minute block, and we confirm a fixed quote with you before any extra work starts. Rekeying is from $${rates.rekeyPerLockFrom} + GST per lock on top of the call-out. ${afterHoursRateLine}`,
  },
  {
    id: "r2",
    question: "Should I rekey my locks or replace them?",
    answer:
      "If your locks are in good condition and you just need old keys to stop working — after moving house, losing keys, or a tenancy change — rekeying is usually the cheaper fix. If the lock itself is worn, sticking, or outdated, replacement is the better long-term option. We'll tell you straight which one you actually need.",
  },
  {
    id: "r3",
    question: "Do you have a shop I can come to?",
    answer: business.workshop.open
      ? `Yes, for key cutting and collecting orders: our workshop is in ${business.baseSuburb}, open ${business.workshop.hoursLine}. Call ahead so we're ready for you. Everything else is mobile — our vans come to you across the North Shore and wider Auckland.`
      : `We're based in ${business.baseSuburb} and work as a mobile service — our vans come to you, fully equipped for on-site work across the North Shore and wider Auckland. That's how we keep response simple and pricing clear.`,
  },
  {
    id: "r5",
    question: "Do you do car keys or safes?",
    answer:
      "No car keys — automotive keys need specialist machinery, so we leave those to the car-key crowd. Safes, yes: we supply, position and bolt down home and business safes properly. (We don't open or service safe locking mechanisms.)",
  },
  {
    id: "r4",
    question: "What hours do you operate?",
    answer:
      `Lockouts and emergency call-outs 24/7, including weekends and public holidays — call any time and we'll get to you as fast as we can. Everything else (rekeying, repairs, new locks) is ${siteConfig.businessHoursLine}. ${afterHoursRateLine}`,
  },
];

// ---------------------------------------------------------------------------
// Service pages. gated: true = requires the qualified locksmith (plan ⛔ pages):
// built now, published at Phase B via siteConfig.locksmithLive.
// ---------------------------------------------------------------------------

const callCta = { type: "call", label: "Call now" };
const quoteCta = { type: "quote", label: "Request a quote" };

export const servicePages = {
  residential: {
    "lock-rekeying": {
      slug: "lock-rekeying",
      gated: true,
      title: "Lock Rekeying",
      metaTitle: "Lock Rekeying North Shore | Shore Doors Locksmiths",
      metaDescription:
        "Rekey your locks so old keys stop working — after moving house, lost keys or a tenancy change. Mobile locksmiths, North Shore Auckland. From $60 + GST per lock.",
      heroSub:
        "Make old keys useless without replacing your locks. Ideal after moving house, losing keys, or a change of tenants or flatmates.",
      intro:
        "Rekeying changes the pins inside your existing lock so it works with a brand-new key — and nothing else. It's usually faster and cheaper than replacing hardware that's still in good condition, and we can key multiple doors alike so one key runs the whole house.",
      scopeTitle: "When rekeying is the right call",
      scope: [
        "You've just moved into a new home and don't know who has keys",
        "Keys lost or stolen — or not returned after a tenancy",
        "After a break-in, alongside any repairs needed",
        "Keying your doors alike so one key opens everything",
      ],
      showRates: true,
      priceNote: `Rekeying from $${rates.rekeyPerLockFrom} per lock + call-out.`,
      image: "/images/work/mortice-thumbturn-wooden-door.jpeg",
      imageAlt: "Mortice lock with handle and thumb turn fitted to a wooden door",
      primaryCta: callCta,
      crossLinks: [
        { to: "/residential/lock-repair-replacement", label: "Lock repair & replacement" },
        { to: "/residential/home-security-upgrades", label: "Home security upgrades" },
      ],
    },
    "lock-repair-replacement": {
      slug: "lock-repair-replacement",
      gated: false,
      title: "Lock Repair & Replacement",
      metaTitle: "Lock Repair & Replacement North Shore | Shore Doors Locksmiths",
      metaDescription:
        "Sticking, jammed or broken locks repaired or replaced by commercial-grade installers. Mobile service across the North Shore and Auckland. Fixed quotes.",
      heroSub:
        "Sticking, jammed, worn or broken locks — repaired where possible, replaced with quality hardware where not. The same installers trusted on hospitals and banks, at your front door.",
      intro:
        "Our team fits and services commercial-grade door hardware every working day. For your home that means locks that are chosen properly, fitted properly, and still working smoothly years later — not a handyman guess. We supply and fit trusted brands, sort door alignment and latch problems while we're there, and back the work with a 12-month workmanship guarantee. Changing all your locks after moving in or a break-in? We do that too, keyed to one new key.",
      scopeTitle: "What we repair and replace",
      scope: [
        "Entrance sets, deadbolts and mortice locks",
        "Sliding and ranchslider door locks",
        "Window catches and locks",
        "Handles, latches and hinges — plus door alignment fixes",
        "Changing every lock in the house after a move or a break-in",
        "Upgrades to higher-security or smart hardware",
      ],
      showRates: true,
      image: "/images/work/cavity-slider-face.jpeg",
      imageAlt: "Residential cavity slider with recessed flush pull and lock, installed by Shore Doors",
      primaryCta: callCta,
      crossLinks: [
        { to: "/residential/sliding-door-locks", label: "Ranchslider & sliding door locks" },
        { to: "/residential/door-handle-repair", label: "Door handle repair" },
        { to: "/residential/lock-rekeying", label: "Lock rekeying" },
      ],
    },
    "key-cutting": {
      slug: "key-cutting",
      gated: true,
      title: "Key Cutting",
      metaTitle: "Mobile Key Cutting North Shore | Shore Doors Locksmiths",
      metaDescription:
        "Keys cut at your place from our mobile van — house, window and padlock keys, keyed-alike options. North Shore Auckland locksmiths.",
      heroSub: business.workshop.open
        ? `Keys cut on the spot from our fully equipped van, or at our ${business.baseSuburb} workshop — not a mall kiosk.`
        : "Keys cut on the spot from our fully equipped van — at your home, not a mall kiosk.",
      intro:
        "We cut house, window and padlock keys on site, so you get spares that actually work, cut from the lock rather than a worn copy where possible. Need one key for several doors? Ask about keying alike while we're there. One thing we don't do: car keys — they need specialist automotive machinery." +
        (business.workshop.open
          ? ` Prefer to come to us? Our ${business.baseSuburb} workshop cuts keys ${business.workshop.hoursLine} — call ahead so we're ready for you.`
          : ""),
      scopeTitle: "What we cut",
      scope: [
        "House and entrance-door keys",
        "Window and patio-door keys",
        "Padlock keys",
        "Spare sets cut from the lock for accuracy",
      ],
      showRates: true,
      priceNote: `Standard keys $${rates.keyCutFrom}–$${rates.keyCutTo} each on an existing visit. Restricted or high-security keys belong to a key system — see Master Key Systems.`,
      primaryCta: callCta,
      crossLinks: [
        { to: "/commercial/master-key-systems", label: "Master key systems" },
        { to: "/residential/lock-rekeying", label: "Lock rekeying" },
      ],
    },
    "home-security-upgrades": {
      slug: "home-security-upgrades",
      gated: false,
      title: "Home Security Upgrades",
      metaTitle: "Home Security Upgrades North Shore | Shore Doors Locksmiths",
      metaDescription:
        "Deadlocks, window locks, sliding-door locks and smart locks — supplied and fitted by professional installers. North Shore & Auckland mobile service.",
      heroSub:
        "Practical security that actually gets used: deadbolts and deadlocks, window locks, sliding-door hardware and smart locks — supplied, fitted and set up properly.",
      intro:
        "We install electronic locking hardware in commercial buildings for a living, so a smart lock or a full set of window locks at your place is comfortably inside the day job. We'll walk your home's entry points with you, recommend what's worth doing (and what isn't), and fit it to insurance-friendly standard.",
      scopeTitle: "Popular upgrades",
      scope: [
        "Deadbolts, deadlocks and high-security cylinders on entry doors",
        "Window locks throughout — often an insurance requirement",
        "Sliding and ranchslider door locks and bolts",
        "Smart locks and keyless entry, set up and demonstrated",
        "Garage, side-gate and shed hardware",
        "Home safes — supplied, positioned and bolted down properly",
      ],
      showRates: true,
      image: "/images/work/digital-lock-outside.jpeg",
      imageAlt: "Digital door lock installed by Shore Doors — exterior side",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/residential/deadbolt-installation", label: "Deadbolt installation" },
        { to: "/residential/smart-lock-installation", label: "Smart & digital locks" },
        { to: "/residential/lock-repair-replacement", label: "Lock repair & replacement" },
      ],
    },
    "deadbolt-installation": {
      slug: "deadbolt-installation",
      gated: false,
      title: "Deadbolt Installation & Replacement",
      metaTitle: "Deadbolt Installation & Replacement North Shore | Shore Doors",
      metaDescription:
        "Deadbolts (deadlocks) supplied, fitted and replaced across Auckland's North Shore — new holes cut or old ones reused, keyed alike to your other doors.",
      heroSub:
        "A deadbolt — most Kiwis call it a deadlock — is the lock that stops a door being slipped or forced. We supply and fit them, replace worn ones, and key them to match your other locks.",
      intro:
        "A good deadbolt is the most effective security upgrade for a front or back door. We fit single-cylinder deadbolts (key outside, thumb-turn inside) and double-cylinder models where there's glass near the lock, using the existing holes where the new lock lines up and cutting clean new ones where it doesn't. Every deadbolt goes in square, with the strike plate set properly into the frame so the bolt throws all the way — a misaligned strike is the most common fault we see.",
      scopeTitle: "What we do",
      scope: [
        "New deadbolts fitted to timber and solid-core doors — and the right mortice deadlock for aluminium joinery",
        "Worn, sticking or seized deadbolts replaced",
        "Strike plates reset so the bolt throws fully into the frame",
        "Keyed alike to your other locks, so one key does the house",
        "Double-cylinder deadbolts where there's glass within reach of the lock",
        "Digital and smart deadbolts fitted and set up",
      ],
      details: [
        {
          h2: "Deadbolt, deadlock or deadlatch?",
          text: "In New Zealand the words get mixed up. A deadbolt is a separate bolt, turned by a key or thumb-turn, that can't be pushed back from outside — most Kiwis call it a deadlock, and many mortice locks on aluminium doors are deadlocks too. A deadlatch is different: a spring latch that locks itself when the door shuts, and can usually be deadlocked with the key. Not sure what's on your door? Text a photo to 022 513 3957 and we'll tell you.",
        },
        {
          h2: "Which deadbolt should you choose?",
          text: "For most homes, a quality single-cylinder deadbolt from a known brand is the right answer. If there's glass within arm's reach of the lock, a double-cylinder model stops someone reaching in to turn the thumb-turn — but it needs a key to get out, so keep one near the door. Keyless digital deadbolts suit families and rentals, as long as the door is sheltered from direct rain.",
        },
      ],
      faqs: [
        {
          q: "Can you fit a deadbolt to my existing door?",
          a: "Almost always. On timber doors we cut the holes and set the strike plate into the frame. Aluminium doors usually need a mortice deadlock rather than a round deadbolt — we'll tell you which fits before any work starts.",
        },
        {
          q: "Can the new deadbolt use the same key as my other locks?",
          a: "In most cases, yes. We can key it alike to your existing locks, or rekey the whole house to one new key.",
        },
      ],
      showRates: true,
      priceNote: "Fitting is charged as call-out plus time, with the deadbolt quoted before we start.",
      image: "/images/stock/deadbolt-installation.png",
      imageAlt: "Deadbolt being installed on a front door",
      primaryCta: callCta,
      crossLinks: [
        { to: "/residential/lock-rekeying", label: "Lock rekeying" },
        { to: "/residential/home-security-upgrades", label: "Home security upgrades" },
        { to: "/blog/deadbolt-deadlock-deadlatch", label: "Deadbolt vs deadlock vs deadlatch" },
      ],
    },
    "sliding-door-locks": {
      slug: "sliding-door-locks",
      gated: false,
      title: "Ranchslider & Sliding Door Locks",
      metaTitle: "Ranchslider Lock Repair & Replacement North Shore | Shore Doors",
      metaDescription:
        "Ranchslider and sliding door locks repaired, replaced and upgraded across Auckland's North Shore — sticking hooks, worn cylinders, keyed and keyless locks.",
      heroSub:
        "Ranchslider won't lock, key won't turn, or the handle's loose? We repair and replace sliding door locks and hooks on aluminium and timber sliders.",
      intro:
        "Ranchsliders are on almost every Kiwi home, and the lock is often the weakest point. Most sliding-door problems come down to a worn cylinder, a hook that no longer lines up with its keeper, or rollers that have dropped so the door sits low. We fix the cause, not just the lock: adjust the rollers and keepers, replace cylinders and lock bodies, and add extra security where the door needs it.",
      scopeTitle: "What we fix and fit",
      scope: [
        "Sliding door locks that won't lock or unlock",
        "Worn or seized cylinders replaced and keyed to your other locks",
        "Hooks and keepers realigned, rollers adjusted so the door sits true",
        "Replacement lock bodies and handle sets for aluminium joinery",
        "Extra security: patio bolts, secondary locks and anti-lift blocks",
        "Keyless and digital locks for sliding doors",
      ],
      details: [
        {
          h2: "Repair or replace?",
          text: "If the handle set is sound and parts are still made for it, a new cylinder or keeper and a roller adjustment usually fixes it. If the lock body is worn out, the model is discontinued, or you want better security, a new lock set is the better spend. We'll tell you which on the day.",
        },
        {
          h2: "Aluminium joinery",
          text: "Many North Shore homes have aluminium joinery from the 1970s to the 2000s, and the parts vary by joinery maker. We carry the common cylinders and hooks in the van and source the rest.",
        },
      ],
      faqs: [
        {
          q: "My ranchslider key turns but the door won't lock. What's wrong?",
          a: "Usually the hook isn't reaching its keeper — the door has dropped on worn rollers, or the keeper has moved. It's often a roller adjustment and a keeper reset rather than a new lock.",
        },
        {
          q: "Can I have a keyless lock on a sliding door?",
          a: "Yes — there are digital locks made for sliding doors. Fitting one is more involved than on a hinged door, so we check the door and frame first.",
        },
      ],
      showRates: true,
      priceNote: "Call-out plus time, with any parts quoted before we start.",
      image: "/images/work/electronic-sliding-door-hook-lock.jpeg",
      imageAlt: "Hook lock fitted to a sliding door by Shore Doors",
      primaryCta: callCta,
      crossLinks: [
        { to: "/residential/lock-repair-replacement", label: "Lock repair & replacement" },
        { to: "/blog/ranchslider-wont-lock", label: "Ranchslider won't lock?" },
      ],
    },
    "door-handle-repair": {
      slug: "door-handle-repair",
      gated: false,
      title: "Door Handle Repair & Replacement",
      metaTitle: "Door Handle Repair & Replacement North Shore | Shore Doors",
      metaDescription:
        "Loose, drooping or broken door handles repaired or replaced across Auckland's North Shore — levers, knobs, entrance sets and sliding door handles.",
      heroSub:
        "Loose, drooping, stiff or snapped handles fixed properly — levers, knobs, entrance sets and sliding door pulls.",
      intro:
        "A handle that droops or wobbles usually has a worn spring or spindle, or loose fixings that have chewed out the door. Left alone, it wears out the latch as well. We tighten, re-spring or replace the handle, fix the latch while we're there, and match the finish and backset so the new set looks and works right.",
      scopeTitle: "What we repair and replace",
      scope: [
        "Drooping levers and loose knobs",
        "Broken spindles, springs and return mechanisms",
        "Entrance handle sets replaced, with locks keyed to your other doors",
        "Passage, privacy and bathroom sets",
        "Sliding and cavity door pulls and handles",
        "Commercial lever sets on high-traffic doors",
      ],
      details: [
        {
          h2: "Matching what you have",
          text: "Handles are sized to the door and the lock: the backset (the distance from the door edge to the centre of the handle), the door thickness and the lock type all matter. We measure first, so a new set fits the existing holes where possible and you're not left with gaps or old screw holes showing.",
        },
      ],
      faqs: [
        {
          q: "Can you replace just the handle and keep my lock?",
          a: "Often, yes — if the lock body is sound and a compatible handle set is available. If the lock is worn too, we'll show you and quote both.",
        },
      ],
      showRates: true,
      priceNote: "Call-out plus time, with the handle set quoted before we start.",
      image: "/images/stock/cover-lock-wooden-door.jpg",
      imageAlt: "Lever handles and mortice lock fitted to a timber door",
      primaryCta: callCta,
      crossLinks: [
        { to: "/residential/lock-repair-replacement", label: "Lock repair & replacement" },
        { to: "/residential/sliding-door-locks", label: "Ranchslider & sliding door locks" },
      ],
    },
    "broken-key-extraction": {
      slug: "broken-key-extraction",
      gated: false,
      title: "Stuck or Broken Key Removal",
      metaTitle: "Broken Key Extraction North Shore | Shore Doors Locksmiths",
      metaDescription:
        "Key snapped in the lock or stuck fast? We extract broken keys across Auckland's North Shore, usually without damaging the lock, and cut you a new key.",
      heroSub:
        "Key snapped off in the lock, or stuck and won't turn? Don't force it. We extract it, check the lock, and cut you a new key.",
      intro:
        "Keys break when they're worn thin, bent, or forced in a lock that's binding. We remove the broken piece with extraction tools, usually without damaging the lock, then find out why it happened. A worn key gets replaced with a fresh cut; a lock that's binding gets cleaned, adjusted or replaced so it doesn't happen again.",
      scopeTitle: "What we do",
      scope: [
        "Snapped keys extracted from house, window and padlock locks",
        "Keys stuck in the lock freed without damage where possible",
        "New keys cut from the lock, not from a worn copy",
        "Binding or worn locks serviced or replaced",
        "Office, shop and gate locks too",
      ],
      details: [
        {
          h2: "Before we arrive",
          text: "Don't push the broken piece back in or try to turn the lock with the other half of the key — that usually jams it deeper. If part of the key is sticking out, you can try gripping it with needle-nose pliers and pulling straight out, without twisting. If the door is locked and it's your only way in, tell us when you call.",
        },
      ],
      faqs: [
        {
          q: "Will I need a new lock?",
          a: "Usually not. Most locks are fine once the broken piece is out. If the key broke because the lock is worn or binding, we'll show you and quote a repair or replacement.",
        },
      ],
      showRates: true,
      priceNote: "Call-out plus time. We quote before any extra work, and a new key is cut on the spot.",
      image: "/images/stock/emergency-door-lock.jpg",
      imageAlt: "Key in a door lock",
      primaryCta: callCta,
      crossLinks: [
        { to: "/emergency-locksmith", label: "Emergency locksmith" },
        { to: "/residential/key-cutting", label: "Key cutting" },
        { to: "/blog/key-snapped-in-lock", label: "Key snapped in the lock?" },
      ],
    },
    "smart-lock-installation": {
      slug: "smart-lock-installation",
      gated: false,
      title: "Smart & Digital Lock Installation",
      metaTitle: "Smart Lock & Digital Lock Installation North Shore | Shore Doors",
      metaDescription:
        "Smart and digital door locks installed and set up across Auckland's North Shore — keypad, fingerprint and app locks on timber and aluminium doors.",
      heroSub:
        "Keypad, fingerprint and app-controlled locks fitted by the team that installs electronic locking in commercial buildings — and set up before we leave.",
      intro:
        "Most smart-lock problems come from the fit, not the lock: a digital lock needs the door to close cleanly and the latch to line up, or it drains batteries and jams. We check the door first, fit the lock square, set up codes, fingerprints and the app with you, and show everyone in the house how it works.",
      scopeTitle: "What we fit and set up",
      scope: [
        "Keypad, fingerprint and app locks for front and back doors",
        "Digital locks for aluminium doors with narrow frames",
        "Smart locks that work with smart-home systems such as Apple Home",
        "Codes, fingerprints and the app set up with you",
        "Replacing a failing or outdated digital lock",
        "Keyless entry for rentals and short-stay hosts",
      ],
      details: [
        {
          h2: "Digital or smart?",
          text: "A digital lock opens with a code, fingerprint or card, and runs on its own app if it has one. A smart lock connects to a smart-home system, so you can lock up, share access and check the door from your phone. Both need a door that closes cleanly and a lock that's sheltered from direct rain.",
        },
      ],
      faqs: [
        {
          q: "What happens if the batteries go flat?",
          a: "You get plenty of low-battery warnings first, and most digital locks have a key override or an emergency power point on the outside. We'll show you yours when we set it up.",
        },
      ],
      showRates: true,
      priceNote: "Fitting is charged as call-out plus time, with the lock quoted before we start.",
      image: "/images/work/digital-lock-inside.jpeg",
      imageAlt: "Digital lock fitted to a residential door by Shore Doors",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/residential/deadbolt-installation", label: "Deadbolt installation" },
        { to: "/residential/home-security-upgrades", label: "Home security upgrades" },
      ],
    },
  },

  commercial: {
    "commercial-locksmith": {
      slug: "commercial-locksmith",
      gated: true,
      title: "Commercial Locksmith Services",
      metaTitle: "Commercial Locksmith Auckland | Shore Doors Locksmiths",
      metaDescription:
        "Business lockouts, rekeying after staff changes, restricted keys and shopfront hardware — from the installers Auckland's security companies already use.",
      heroSub:
        "Lockouts, rekeys and key control for businesses, landlords and property managers — delivered by the team that installs commercial hardware every day.",
      intro:
        "Staff change, keys go missing, tenants move on. We rekey and re-secure commercial premises with minimal disruption, keep key control tidy with restricted profiles, and fix the hardware problems we find while we're there — because commercial door hardware is our core trade.",
      scopeTitle: "What we handle",
      scope: [
        "Business lockouts and urgent re-securing",
        "Rekeying after staff or tenancy changes",
        "Restricted key profiles and key management",
        "Shopfront, office and back-of-house lock hardware",
        "Landlord and property-manager support across portfolios",
      ],
      image: "/images/stock/commercial-locksmith-banner.jpg",
      imageAlt: "Commercial locksmithing services — Shore Doors Locksmiths",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/commercial/master-key-systems", label: "Master key systems" },
        { to: "/commercial/maintenance-iqp", label: "Maintenance & IQP support" },
      ],
    },
    "door-hardware-installation": {
      slug: "door-hardware-installation",
      gated: false,
      title: "Commercial Door Hardware Installation",
      metaTitle: "Commercial Door Hardware Installation Auckland | Shore Doors Locksmiths",
      metaDescription:
        "Mortice locks, panic hardware, door closers and fire-door hardware installed to NZ Building Code by Auckland's specialist install team.",
      heroSub:
        "Commercial mortice locks, panic and exit hardware, closers and fire-door hardware — installed for durability, compliance and minimal callbacks.",
      intro:
        "This is the work Shore Doors is known for. We install and service commercial-grade mechanical door hardware built to withstand daily use in demanding environments — hospitals, schools, government buildings, corporate offices. New fit-out or replacement programme, the hardware is selected and installed for long-term reliability, with fire-door and Building Code requirements handled properly.",
      scopeTitle: "Installation scope",
      scope: [
        "Commercial mortice locks, cylinders and levers",
        "Panic and exit hardware for compliant egress",
        "Door closers and door control hardware",
        "Fire-door hardware to code and insurance requirements",
        "Hinges, seals and door furniture",
        "Swing, sliding and gate automation",
      ],
      image: "/images/work/crashbar-kickplates.jpeg",
      imageAlt: "Crash bar with kick plates installed by Shore Doors",
      video: "/videos/gate-closer-maglock-install.mp4",
      videoCaption: "Heavy-duty gate closer installed on a heavy steel gate, with maglock — one of ours.",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/commercial/door-closers", label: "Door closers" },
        { to: "/commercial/access-control-installation", label: "Access control installation" },
        { to: "/commercial/maintenance-iqp", label: "Maintenance & IQP support" },
      ],
    },
    "door-closers": {
      slug: "door-closers",
      gated: false,
      title: "Door Closer Installation & Repair",
      metaTitle: "Door Closer Installation & Repair Auckland | Shore Doors",
      metaDescription:
        "Door closers supplied, installed, adjusted and replaced for Auckland businesses and homes — fire doors, gates and busy entries, set to close and latch properly.",
      heroSub:
        "Doors that slam, won't latch or drift open — closers fitted, adjusted and replaced so the door closes properly every time.",
      intro:
        "A door closer has two jobs: close the door at a safe speed, and make sure it latches. On fire doors that's a compliance requirement, not a nicety. We install and service surface-mounted, concealed and gate closers in commercial buildings as part of our everyday trade work, and bring the same care to shop doors, apartment entries and homes.",
      scopeTitle: "What we fit and fix",
      scope: [
        "Surface-mounted, concealed and transom closers",
        "Fire and smoke door closers set to latch every time",
        "Slamming, leaking or drifting closers adjusted or replaced",
        "Hold-open arms and door stops where they're permitted",
        "Gate closers for pool and security gates",
        "Opening force set for easier access, or an automatic operator where it's needed",
      ],
      details: [
        {
          h2: "Fire doors and compliance",
          text: "Fire and smoke doors need a closer that shuts and latches the door from any open position. They're often part of the building's compliance schedule, so we fit and adjust closers to the door's requirements and can record the work for your Building Warrant of Fitness.",
        },
      ],
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/commercial/door-hardware-installation", label: "Door hardware installation" },
        { to: "/commercial/door-automation", label: "Automatic door openers" },
        { to: "/commercial/maintenance-iqp", label: "Maintenance & IQP support" },
      ],
    },
    "access-control-installation": {
      slug: "access-control-installation",
      gated: false,
      title: "Access Control Hardware Installation",
      metaTitle: "Access Control Installation Auckland | Shore Doors Locksmiths",
      metaDescription:
        "Maglocks, electric strikes, drop bolts, Aperio wireless locks, cabling and commissioning — installed to your integrator's specification. Auckland-wide delivery partner.",
      heroSub:
        "The locking layer of access control — maglocks, electric strikes, drop bolts, electronic mortice locks and wireless locks — installed clean and commissioned properly. (Readers and system programming stay with your integrator.)",
      intro:
        "We install the hardware that makes access control systems work, to the specification set by your integrator or project team. That includes low-voltage cabling and power with battery backup, full termination and labelling, and testing and handover documentation for every access point. We work alongside your access control provider — supporting your project team, not replacing them.",
      scopeTitle: "Installation scope",
      scope: [
        "Maglocks, electric strikes and drop bolts",
        "Electronic mortice locks and wireless locks (e.g. Aperio)",
        "Low-voltage cabling, power supplies and battery backup",
        "Commissioning, testing and as-built handover documentation",
        "New builds and retrofit projects",
      ],
      image: "/images/work/double-maglock-hospital.jpeg",
      imageAlt: "Double maglock installed into a hospital double-door frame by Shore Doors",
      video: "/videos/v-lock-glass-doors-install.mp4",
      videoCaption: "Electronic V-Lock installation on commercial glass double doors.",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/partners", label: "Work with us as your install partner" },
        { to: "/commercial/door-hardware-installation", label: "Door hardware installation" },
      ],
    },
    "door-automation": {
      slug: "door-automation",
      gated: false,
      title: "Door Automation & Accessibility — AHS A200",
      metaTitle: "Automatic Door Openers Auckland — Certified AHS A200 Installer | Shore Doors Locksmiths",
      metaDescription:
        "Auckland's only business certified to install and service the AHS A200 door actuator. Automatic door opening for accessibility — regularly delivered for disabled customers through ACC.",
      heroSub:
        "Automatic door opening for accessibility and convenience. We are the only business in Auckland certified to install and service the AHS A200 door actuator — and we fit them regularly for disabled customers through ACC.",
      intro:
        "An automatic door can be the difference between needing help and having independence. We supply, install and service low-energy swing door operators — led by the AHS A200 door actuator, which we're uniquely certified for in Auckland — for homes, healthcare facilities and businesses. For disabled customers, this is work we regularly deliver through ACC-supported home modifications, working in with assessors and occupational therapists.",
      scopeTitle: "What we deliver",
      scope: [
        "AHS A200 door actuator — certified supply, installation and servicing (Auckland's only certified business)",
        "Automatic door openers for wheelchair users and accessibility — homes and workplaces",
        "ACC-supported accessibility installs, coordinated with assessors and OTs",
        "Activation options to suit: push pads, remotes and hands-free operation",
        "Swing door operators, sliding doors and gate automation for commercial sites",
        "Automatic door repair and ongoing servicing to keep openers safe, compliant and reliable",
      ],
      image: "/images/work/a200-custom-paint.jpeg",
      imageAlt: "AHS A200 door actuator with custom paint finish, installed by Shore Doors",
      heroImageRight: true, // small source image — right 60% of the banner with the standard left fade
      videos: [
        { src: "/videos/a200-door-opener-demo.mp4", caption: "The AHS A200 door opener in action — from one of our installs." },
        { src: "/videos/a200-swing-door-2.mp4", caption: "Another AHS A200 swing-door install." },
        { src: "/videos/a200-push-and-go-function.mp4", caption: "Push & Go on a retirement-village fire door: the mechanical lock stays, the operator assists the opening." },
      ],
      primaryCta: callCta,
      crossLinks: [
        { to: "/commercial/door-hardware-installation", label: "Door hardware installation" },
        { to: "/residential/home-security-upgrades", label: "Home security upgrades" },
      ],
    },
    "master-key-systems": {
      slug: "master-key-systems",
      gated: true,
      title: "Master Key Systems",
      metaTitle: "Master Key Systems Auckland | Shore Doors Locksmiths",
      metaDescription:
        "Master key and restricted key systems designed, installed and managed for schools, offices, body corporates and property portfolios across Auckland.",
      heroSub:
        "One key for management, the right keys for everyone else — designed, documented and kept under control with restricted profiles.",
      intro:
        "A good key system means people open exactly what they should and nothing they shouldn't, and lost keys stop being a crisis. We design master key hierarchies around how your site actually runs, build them on restricted keyways so keys can't be copied at a kiosk, and keep a proper key register so control survives staff turnover.",
      scopeTitle: "What we deliver",
      scope: [
        "Master key system design for new and existing sites",
        "Restricted keyways — copies only on your written authority",
        "Keyed-alike and sub-master structures for departments and tenancies",
        "Key registers and signing procedures",
        "Staged rollouts that work around your occupants",
      ],
      image: "/images/projects/auckland-city-hospital.webp",
      imageAlt: "Auckland City Hospital — key systems across multiple areas",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/commercial/commercial-locksmith", label: "Commercial locksmith services" },
        { to: "/commercial/maintenance-iqp", label: "Maintenance & IQP support" },
      ],
    },
    "maintenance-iqp": {
      slug: "maintenance-iqp",
      gated: false,
      // Hugh, 26 Sep 2026: not on Auckland Council's IQP register — IQP SUPPORT only. Never claim
      // to carry out or sign off IQP inspections.
      title: "IQP Support, BWOF Remedials & Door Maintenance",
      metaTitle: "IQP Support & Door Maintenance Auckland | Shore Doors Locksmiths",
      metaDescription:
        "Door maintenance checks, fire-door and automatic door repairs, and the remedial work IQP inspections turn up — working alongside your IQP and access control provider.",
      heroSub:
        "Keep doors, automatic openers and access-control hardware working and compliant — scheduled maintenance checks, the remedial work IQP inspections turn up, and support for your Building Warrant of Fitness (BWOF).",
      intro:
        "Doors are the highest-wear moving parts in most buildings — and several of them are specified systems that have to stay compliant. We don't sign off IQP inspections ourselves; we work alongside your IQP, integrator and facilities team. That means scheduled maintenance checks on automatic and electronic sliding doors, fire and smoke doors and their hardware, and final exit doors, plus the repairs and remedial work that keep them passing inspection. Fire-door hardware is our everyday trade, so failed closers, latches and exit devices get fixed properly. Planned maintenance agreements available for single sites or portfolios.",
      scopeTitle: "Programme scope",
      scope: [
        "Scheduled preventive maintenance visits",
        "IQP support: remedial work from inspections, and records for your BWOF",
        "Automatic and electronic sliding door maintenance checks",
        "Fire-door, smoke-door and final-exit hardware checks and remedials",
        "Fault-finding on access hardware, door closers and panic/exit devices",
        "Condition reporting across property portfolios",
      ],
      image: "/images/projects/papakura-library.webp",
      imageAlt: "Papakura Library — door hardware maintained by Shore Doors",
      primaryCta: quoteCta,
      crossLinks: [
        { to: "/commercial/door-hardware-installation", label: "Door hardware installation" },
        { to: "/partners", label: "Trade partners" },
      ],
    },
  },
};

// Recent-work photo strip on /projects — real install photos from the team
export const workGallery = [
  { src: "/images/work/black-maglock-custom-cable-box.jpeg", caption: "Commercial maglock — custom aluminium bracket and cover plate we fabricated to conceal the ceiling cable run" },
  { src: "/images/work/gate-maglock-closed.jpeg", caption: "Custom atrium gate — maglock flush-mounted by our team" },
  { src: "/images/work/gate-maglock-flushmounted.jpeg", caption: "Same gate: maglock recessed flush into the post" },
  { src: "/images/work/gate-maglock-armature.jpeg", caption: "Same gate: armature plate detail" },
  { src: "/images/work/pinpad-steel-gate.jpeg", caption: "Pinpad lock on a steel gate with guard plate" },
  { src: "/images/work/electronic-sliding-door-hook-lock.jpeg", caption: "Electronic hook lock on a sliding door" },
  { src: "/images/work/strike-lock-wooden-frame.jpeg", caption: "Electric strike fitted to a wooden frame" },
  { src: "/images/work/double-maglock-hospital.jpeg", caption: "Double maglock installed into a hospital double-door frame" },
  { src: "/images/work/cavity-slider-edge.jpeg", caption: "Residential cavity slider — mortice edge detail" },
  { src: "/images/work/digital-lock-inside.jpeg", caption: "Residential digital lock (Schlage) — mortice edge detail" },
  { src: "/images/work/crashbar-kickplates.jpeg", caption: "Crash bar with kick plates" },
  { src: "/images/work/a200-custom-paint.jpeg", caption: "AHS A200 door actuator — custom paint to match the joinery" },
];

// Hub card listings (order = display order)
// bg = faded card background — Hugh's chosen images (5 Jul 2026)
export const residentialCards = [
  { slug: "lock-repair-replacement", blurb: "Sticking, worn or broken locks repaired or replaced with quality hardware.", icon: "Wrench", bg: "/images/stock/deadbolt-installation.png" },
  { slug: "home-security-upgrades", blurb: "Deadlocks, window locks, sliding-door locks and smart locks — fitted properly.", icon: "Shield", bg: "/images/stock/home-security-digital-lock.png" },
  { slug: "lock-rekeying", blurb: "New keys, same locks — after moving house, lost keys or a tenancy change.", icon: "KeyRound", bg: "/images/stock/rekeying-cylinder-keys.png" },
  { slug: "key-cutting", blurb: "Keys cut at your place from our mobile van.", icon: "Key", bg: "/images/stock/key-cutting-man.webp" },
  { slug: "deadbolt-installation", blurb: "Deadbolts and deadlocks fitted, replaced and keyed to match your other locks.", icon: "Lock", bg: "/images/stock/residential-lock-fitting.jpg" },
  { slug: "sliding-door-locks", blurb: "Ranchslider and sliding door locks repaired, replaced and made secure.", icon: "DoorOpen", bg: "/images/work/electronic-sliding-door-hook-lock.jpeg" },
  { slug: "smart-lock-installation", blurb: "Keypad, fingerprint and app locks fitted and set up with you.", icon: "Fingerprint", bg: "/images/work/digital-lock-inside.jpeg" },
  { slug: "door-handle-repair", blurb: "Loose, drooping or broken handles fixed or replaced.", icon: "Wrench", bg: "/images/stock/cover-lock-wooden-door.jpg" },
];

export const commercialCards = [
  { slug: "door-hardware-installation", blurb: "Mortice locks, panic hardware, closers and fire-door hardware to code.", icon: "DoorOpen" },
  { slug: "access-control-installation", blurb: "Maglocks, electric strikes, drop bolts and wireless locks, installed to your integrator's spec.", icon: "Lock" },
  { slug: "door-automation", blurb: "Automatic door openers — Auckland's only certified AHS A200 installer; accessibility work through ACC.", icon: "Accessibility" },
  { slug: "maintenance-iqp", blurb: "Scheduled maintenance, IQP support and remedials for facilities teams.", icon: "Settings" },
  { slug: "master-key-systems", blurb: "Restricted master key systems designed and kept under control.", icon: "KeyRound" },
  { slug: "commercial-locksmith", blurb: "Lockouts, rekeys and key control for businesses and property managers.", icon: "Building2" },
];
